import { useState } from "react";

const dateLabel = (value, lang) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString(lang === "es" ? "es-PR" : "en-US", { month: "short", day: "numeric", year: "numeric" });
};

/** What visitors have said, and the box to add to it. */
export default function DetailTips({ labels = {}, reviews = [], form, setForm, onSubmit, submitting, lang }) {
  const [open, setOpen] = useState(false);
  const prompts = lang === "es" ? ["Qué pedir", "Cuándo ir", "Consejo local", "Accesibilidad"] : ["What to order", "Best time to go", "Local tip", "Accessibility"];

  return (
    <section id="reviews" className="scroll-mt-28">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-[16px] font-extrabold tracking-[-0.01em] text-[#171717] dark:text-[#f2efe9]">{labels.heading}</h2>
        {reviews.length > 0 && <span className="text-[11px] font-semibold text-[#8d8d88]">{labels.count}</span>}
      </div>

      <p className="mt-1.5 text-[12px] leading-[1.55] text-[#777773] dark:text-[#c0b9ae]">{lang === "es" ? "Comparte lo que le serviría saber a otra persona: qué pedir, cuándo ir, accesibilidad o un detalle local. No hay estrellas ni calificaciones." : "Share something useful: what to order, when to go, accessibility, or a local secret. No stars, scores, or rankings."}</p>

      {reviews.length === 0 ? (
        <p className="mt-2 text-[13px] leading-6 text-[#777773] dark:text-[#b8b3a9]">{labels.empty}</p>
      ) : (
        <div className="mt-3 space-y-2.5">
          {reviews.map((review) => (
            <article key={review.id} className="rounded-[12px] border border-[#E4E2DC] bg-white p-3.5 dark:border-white/[0.10] dark:bg-[#1d1e1b]">
              <div className="flex items-center gap-2.5">
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#EFEEEA] text-[12px] font-black text-[#55554f] dark:bg-white/[0.09] dark:text-[#ded9d1]">
                  {(review.author_name || "?").trim().charAt(0).toUpperCase()}
                </span>
                <div className="min-w-0">
                  <div className="truncate text-[12.5px] font-bold text-[#24231f] dark:text-[#f0ede6]">{review.author_name || labels.anonymous}</div>
                  {review.created_date && (
                    <div className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-[#8d8d88]">{dateLabel(review.created_date, lang)}</div>
                  )}
                </div>
              </div>
              <p className="mt-2.5 text-[13.5px] leading-[1.7] text-[#3f3f3c] dark:text-[#ddd8d0]">{review.body}</p>
            </article>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="mt-3 inline-flex h-11 w-full items-center justify-center rounded-[10px] border border-[#171717] text-[12.5px] font-bold text-[#171717] transition hover:bg-[#171717] hover:text-white dark:border-[#f2efe9] dark:text-[#f2efe9] dark:hover:bg-[#f2efe9] dark:hover:text-[#171614]"
      >
        {labels.share}
      </button>

      {open && (
        <form onSubmit={onSubmit} className="mt-3 rounded-[12px] border border-[#E4E2DC] bg-white p-3.5 dark:border-white/[0.10] dark:bg-[#1d1e1b]">
          <input
            value={form.author_name}
            onChange={(event) => setForm({ ...form, author_name: event.target.value })}
            placeholder={labels.namePlaceholder}
            className="mb-2 h-11 w-full rounded-[9px] border border-[#E4E2DC] bg-white px-3 text-[13px] text-[#24231f] outline-none placeholder:text-[#b8b8b3] focus:border-[#a7a69f] dark:border-white/[0.14] dark:bg-white/[0.04] dark:text-[#f0ede6] dark:placeholder:text-[#77766f]"
          />
          <div className="mb-3">
            <p className="mb-2 text-[11px] font-bold text-[#68665f] dark:text-[#c5bfb5]">{lang === "es" ? "¿Qué quieres compartir?" : "What would you like to share?"}</p>
            <div className="flex flex-wrap gap-1.5">
              {prompts.map((prompt) => (
                <button key={prompt} type="button" onClick={() => { if (!form.body.trim()) setForm({ ...form, body: `${prompt}: ` }); }} className="rounded-full border border-[#d9d6cf] bg-[#f8f7f3] px-3 py-2 text-[11px] font-semibold text-[#393733] transition hover:border-[#e04445] hover:bg-[#fff4f3] dark:border-white/[0.16] dark:bg-white/[0.06] dark:text-[#eee9e1] dark:hover:bg-white/[0.12]">{prompt}</button>
              ))}
            </div>
          </div>
          <textarea
            value={form.body}
            onChange={(event) => setForm({ ...form, body: event.target.value })}
            placeholder={labels.bodyPlaceholder}
            rows={3}
            maxLength={1000}
            required
            className="w-full rounded-[9px] border border-[#E4E2DC] bg-white px-3 py-2.5 text-[13.5px] text-[#24231f] outline-none placeholder:text-[#b8b8b3] focus:border-[#a7a69f] dark:border-white/[0.14] dark:bg-white/[0.04] dark:text-[#f0ede6] dark:placeholder:text-[#77766f]"
          />
          <button
            disabled={submitting}
            className="mt-2.5 h-11 w-full rounded-[9px] bg-[#171717] text-[12.5px] font-bold text-white transition hover:bg-[#33332f] disabled:opacity-50 dark:bg-[#f2efe9] dark:text-[#171614] dark:hover:bg-white"
          >
            {submitting ? labels.submitting : labels.submit}
          </button>
        </form>
      )}
    </section>
  );
}